/** API canónica de kits bajo rincón (KBR*) — ya no se usa para forzar GLB. */
export const TPC_PLANNER_API_BASE = 'https://planificador3dapi.cocinas-tpc.com'

/** idUnidades resueltos vía API TPC (legado; ya no redirige peticiones). */
const tpcResolvedUnitIds = new Set<number>()

/**
 * Normaliza un código de catálogo quitando prefijo PE y sufijos de acabado/gola.
 * Ej: "PE KBRB8010045 Li GO" → "KBRB8010045"
 */
export function normalizeCatalogCode(catalogID: string | number | null | undefined): string {
  let code = String(catalogID ?? '')
    .trim()
    .toUpperCase()
  if (!code) return ''
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  code = code.replace(/\s+(LI|LA|RU|R2)(\s+GO)?$/i, '').replace(/\s+GO$/i, '').trim()
  return code
}

/**
 * Kits bajo rincón clásicos (KBRB/KBRP…). Política de mallas Mesh001_2 / Alto.
 * No incluye Gola (KGBRB*): ahí Mesh*_2 es Gola/Armazón, no el zócalo L.
 */
export function isKBRCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KBR')
}

/**
 * KBR* y Gola bajo rincón (KGBRB8012060 GO). Misma regleta/sidebar/zócalo tira;
 * el GLB Gola no usa la convención de mallas del KBR.
 */
export function isKbrRinconeroFamilyCatalog(
  catalogID: string | number | null | undefined,
): boolean {
  const code = normalizeCatalogCode(catalogID)
  return code.startsWith('KBR') || code.startsWith('KGBR')
}

/**
 * Rinconero Gola (KGBRB* / KGBRP*…): Fondo = puerta, scaleZ y zócalo L nuevos.
 * KBRB8010040 y resto KBR* (aunque lleven GO de acabado) siguen la lógica clásica.
 */
export function isKbrGolaRinconeroCatalog(
  catalogID: string | number | null | undefined,
): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KGBR')
}

/** KARB* (no usar includes('arb'): KBRB también lo contiene). */
export function isKARBCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KARB')
}

/**
 * Regleta para geometría/snap = valor sidebar/API.
 * KBR/KARB: directa (20→20). Antes KBR invertía max+min−v (20↔120).
 */
export function getRegletaEffectiveMm(
  item: {
    regleta?: number | null
    catalogID?: string | number
    morphs?: Array<{ name: string; min: number; max: number }>
  } | null | undefined,
): number {
  const stored = Number(item?.regleta ?? 0)
  if (!Number.isFinite(stored) || stored <= 0) return 0
  if (isKbrRinconeroFamilyCatalog(item?.catalogID) || isKARBCatalog(item?.catalogID)) return stored
  const morph = item?.morphs?.find((m) => m.name === 'Regleta')
  if (!morph || !(Number(morph.max) > Number(morph.min))) return stored
  if (stored >= morph.max) return morph.min
  if (stored <= morph.min) return morph.max
  return morph.max + morph.min - stored
}

/** Proyectos antiguos: la regleta KBR se interpreta 20 mm más corta de lo actual. */
export const KBR_LEGACY_REGLETA_EXTRA_MM = 20

type KbrRegletaItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  regleta?: number | null
  regletaMinimo?: number | null
  regletaMaximo?: number | null
  kbrLegacyRegletaPlus20Applied?: boolean
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
    min?: number
    max?: number
  }>
}

/**
 * Dato malo en BD: KGBR* guardados con fondo 720. El valor correcto es 600.
 * Quitar este bloque cuando ya no queden proyectos antiguos con 720.
 */
export const KGBR_LEGACY_FONDO_MM = 720
export const KGBR_CORRECT_FONDO_MM = 600

type KgbrFondoItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  thickness?: number
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
  }>
  freeDimensionCatalogMm?: { x?: number; y?: number; z?: number }
}

function kgbrFondoMm(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n) : 0
}

/**
 * Solo KGBR con fondo exactamente 720 → 600 (store + morph Fondo + catálogo).
 * Cualquier otra medida se deja. Idempotente al reabrir si ya está en 600.
 */
export function applyKgbrLegacyFondo720To600(item: KgbrFondoItem | null | undefined): boolean {
  if (!item) return false
  if (
    !isKbrGolaRinconeroCatalog(item.catalogID) &&
    !isKbrGolaRinconeroCatalog(item.codigo) &&
    !isKbrGolaRinconeroCatalog(item.referencia)
  ) {
    return false
  }
  if (kgbrFondoMm(item.thickness) !== KGBR_LEGACY_FONDO_MM) return false

  item.thickness = KGBR_CORRECT_FONDO_MM
  const morph = item.morphs?.find((m) => m.name === 'Fondo')
  if (morph) {
    morph.default = KGBR_CORRECT_FONDO_MM
    morph._default = KGBR_CORRECT_FONDO_MM
  }
  if (
    item.freeDimensionCatalogMm &&
    kgbrFondoMm(item.freeDimensionCatalogMm.z) === KGBR_LEGACY_FONDO_MM
  ) {
    item.freeDimensionCatalogMm.z = KGBR_CORRECT_FONDO_MM
  }
  return true
}

/**
 * Al abrir un JSON antiguo: 60 → 80 (y cualquier valor +20, clamp al max API).
 * Idempotente (`kbrLegacyRegletaPlus20Applied`). No aplicar al dibujar unidades nuevas.
 */
export function applyKbrLegacyRegletaPlus20(item: KbrRegletaItem | null | undefined): boolean {
  if (!item || item.kbrLegacyRegletaPlus20Applied) return false
  if (
    !isKBRCatalog(item.catalogID) &&
    !isKBRCatalog(item.codigo) &&
    !isKBRCatalog(item.referencia)
  ) {
    return false
  }
  const morph = item.morphs?.find((m) => m.name === 'Regleta')
  const min = Number(item.regletaMinimo ?? morph?.min ?? 20)
  const max = Number(item.regletaMaximo ?? morph?.max ?? 120)
  const current = Number(item.regleta ?? morph?.default ?? 0)
  item.kbrLegacyRegletaPlus20Applied = true
  if (!(current > 0) || !Number.isFinite(current)) return false
  const lo = Number.isFinite(min) && min > 0 ? min : 20
  const hi = Number.isFinite(max) && max > lo ? max : 120
  const next = Math.max(lo, Math.min(hi, current + KBR_LEGACY_REGLETA_EXTRA_MM))
  item.regleta = next
  if (morph) {
    morph.default = next
    morph._default = next
  }
  return next !== current
}

export function rememberTpcUnitId(idUnidades: number | null | undefined) {
  const id = Number(idUnidades)
  if (Number.isFinite(id) && id > 0) tpcResolvedUnitIds.add(id)
}

export function isTpcResolvedUnitId(idUnidades: number | null | undefined): boolean {
  const id = Number(idUnidades)
  return Number.isFinite(id) && id > 0 && tpcResolvedUnitIds.has(id)
}

/**
 * Ya no se fuerza planificador3dapi para KBR: el GLB/unidad sale de la API
 * normal del proyecto (getProxyUrl). Las tiras Zocalo 4000 van aparte.
 */
export function resolveUnitApiBaseUrl(
  _catalogID?: string | number | null,
  _idUnidades?: number | null,
): string | null {
  return null
}

/**
 * Antes KBR omitía Zocalo 4000. Ahora KBR usa tira (recta / L frente+retorno).
 */
export function shouldSkipAutoZocalo(_catalogID: string | number | null | undefined): boolean {
  return false
}

/** @deprecated No reescribe a planner; se mantiene por compat. */
export function rewriteToPlannerCatalogUrl(url?: string | null): string | undefined {
  return url ?? undefined
}

/** @deprecated Identity: no reescribir URLs de CatalogData a planner. */
export function rewriteKbrUnitPayloadUrls<T extends Record<string, any>>(json: T): T {
  return json
}
